import * as React from 'react'

import type { Block, TextBlock } from '@basehub/data/replicache/index'

import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { mergeRefs } from '~/lib/merge-refs'
import { $input } from '~/sotano-kit/primitives/input'
import { useSyncedValue } from '~/hooks/use-synced-value'
import { getPlaceholder } from '../utils/get-placeholder'
import { useBlockInputBaseProps } from '../block-wrapper'
import { clsx } from 'clsx'
import TextareaAutosize from 'react-textarea-autosize'
import { TextAreaProps } from '~/sotano-kit/primitives/textarea'
import { Slot } from '@radix-ui/react-slot'
import { useSelectedVariants } from '~/hooks/use-selected-variants'
import {
  getValueBasedOnSelectedVariants,
  getBlockUpdateOverride,
} from '@basehub/data/replicache/index'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useTabsStore } from '../../tabs/store'
import { useEditorMode } from '../../editor-mode-provider'

export const TextBlockInput = ({
  block,
  path,
  parent,
  className,
  maxRows = 10,
  asChild,
  readOnly,
  ...props
}: {
  block: TextBlock
  path: string
  parent: Block
  maxRows?: number
  visible?: boolean
} & Omit<TextAreaProps, 'style'>) => {
  const Comp = asChild ? Slot : TextareaAutosize
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const inputRef = React.useRef<HTMLTextAreaElement>(null)
  const baseProps = useBlockInputBaseProps(block)
  const editorMode = useEditorMode()
  const selectedDashboardVariants = useSelectedVariants({
    parentBlock: parent,
    block,
  })

  const _value = React.useMemo(
    () =>
      getValueBasedOnSelectedVariants({
        block,
        variantsToFilterBy: selectedDashboardVariants,
      }) ?? '',
    [block, selectedDashboardVariants]
  )

  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)

  const { onChange, ref, value } = useSyncedValue<HTMLTextAreaElement>({
    value: _value,
    customEventsKey: `text-block-input-sync-${idToSourceBlockId(block)}`,
    setValue: React.useCallback(
      async (v, { internalValueRef, customEventsKey }) => {
        if (!replicache?.mutate) return
        const getParams = (v: string): UpdateBlockParams => ({
          id: block.id,
          data: getBlockUpdateOverride({
            block,
            newValue: v === '' ? null : v,
            selectedVariants: selectedDashboardVariants,
          }),
          instanceChildTarget: block.instanceChildTarget,
          path,
          strategy: 'merge-deep',
          isoNow: new Date().toISOString(),
        })
        const { undo } = await replicache.mutate.updateBlock(getParams(v))
        if (undo) {
          undoManager.current?.push({
            redo: [
              {
                op: 'focus',
                args: {
                  focus: {
                    type: 'block:value',
                    blockId: idToSourceBlockId({
                      id: block.id,
                      sourceBlockId: block.sourceBlockId,
                    }),
                  },
                  preferredTabKey: activeTabKey,
                },
              },
              { op: 'updateBlock', args: getParams(internalValueRef.current) },
              {
                op: 'customWindowEvent',
                args: { name: customEventsKey, detail: { action: 'sync' } },
              },
            ],
            undo: [
              {
                op: 'focus',
                args: {
                  focus: {
                    type: 'block:value',
                    blockId: idToSourceBlockId({
                      id: block.id,
                      sourceBlockId: block.sourceBlockId,
                    }),
                  },
                  preferredTabKey: activeTabKey,
                },
              },
              { op: 'blockBatchMutations', args: { mutations: undo } },
              {
                op: 'customWindowEvent',
                args: { name: customEventsKey, detail: { action: 'sync' } },
              },
            ],
          })
        }
      },
      [
        block,
        path,
        replicache?.mutate,
        selectedDashboardVariants,
        undoManager,
        activeTabKey,
      ]
    ),
    rateLimitMs: 1000,
  })

  const isReadOnly = readOnly || editorMode === 'read'

  return (
    <>
      <Comp
        {...baseProps}
        readOnly={isReadOnly}
        placeholder={getPlaceholder(block.title)}
        value={value}
        onChange={(e) => 'value' in e.target && onChange(e.target.value)}
        className={clsx($input(), className, 'resize-none py-1.5')}
        data-text-block
        // eslint-disable-next-line react-compiler/react-compiler
        ref={mergeRefs([inputRef, ref])}
        maxRows={maxRows}
        cacheMeasurements
        {...props}
      />
    </>
  )
}
